import Feature from "@/modules/Pricing/components/PlanPricingItem/Feature"
import Promotion from "@/modules/Pricing/components/PlanPricingItem/Promotion"
import { PLAN_NO, PLAN_TYPE, PLAN_ACTION, PLAN_PRICING_DEFAULT } from "@/contants/plan";
import {mapGetters, mapState} from "vuex";

export default () => ({
  components: {
    Feature,
    Promotion
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    planNo: String,
    oldPlan: {
      show: Boolean,
      content: String,
      features: {}
    },
    isCurrentPlan: Boolean,
    isRecommended: Boolean,
    isPricingOnly: Boolean,
    title: String,
    description: String,
    features: Array,
    yearlyMode: Boolean,
    featureDescription: String,
    liveChat: {
      title: String,
      content: String,
    },
    extraData: {},
    expireText: Boolean
  },
  data: () => {
    return {
      chargePlanActionAttribute: ""
    }
  },
  computed: {
    ...mapState("account", {
      plan: state => state.shopInfo.plan,
      account: state => state.shopInfo
    }),
    ...mapGetters("dashboard", {
      isBFCM: "isBFCM",
    }),
    isDevelopmentStore() {
      return this.account.developmentStore
    },
    isProfessionalPlan() {
      return this.planNo === PLAN_NO.BUILD && this.plan === PLAN_NO.PROFESSIONAL && !this.isDevelopmentStore;
    },
    isAdvancePlan() {
      return this.planNo === PLAN_NO.OPTIMIZE && this.plan === PLAN_NO.ADVANCED;
    },
    prepareFeatureDescription() {
      if (this.isDevelopmentStore) {
        return this.featureDescription
      }

      if (this.plan === PLAN_NO.ADVANCED && this.planNo === PLAN_NO.OPTIMIZE) {
        return this.oldPlan.title_feature
      }

      if (this.plan === PLAN_NO.PROFESSIONAL && this.planNo === PLAN_NO.BUILD) {
        return this.oldPlan.title_feature
      }

      return this.featureDescription
    },
    hasCurrentFeatureCondition() {
      return this.extraData?.feature?.conditions.includes(this.plan)
    },
    chargeCurrentPlan() {
      if (this.yearlyMode && !this.maybeChargeYearly) {
        return this.chargePlan(this.planNo)
      }

      if (!this.yearlyMode && !this.maybeChargeMonthly) {
        return this.chargePlan(this.planNo)
      }

      return 'javascript:void(0)'
    },
    maybeChargeYearly() {
      if (!this.paymentInfo.type) return
      return (this.paymentInfo.type).toLowerCase() == PLAN_TYPE.ANNUALLY
    },
    maybeChargeMonthly() {
      if (!this.paymentInfo.type) return
      return (this.paymentInfo.type).toLowerCase() != PLAN_TYPE.ANNUALLY
    },
    findPromotionByPlan() {
      const currentPlan = this.isFreeTrial ? PLAN_NO.TRIAL2022 : this.plan
      if (Object.keys(this.paymentInfo).length <= 0) return

      for (const promotion of this.paymentInfo.promotions) {
        for (const condition of promotion.condition) {
          if (condition.currentPlanName.length > 0 && condition.currentPlanName.includes(currentPlan)) {
            if (condition.planName.includes(this.planNo)) {
              return promotion
            }
          }

          if (condition.currentPlanName.length === 0) {
            if (condition.planName.includes(this.planNo)) {
              return promotion
            }
          }
        }
      }
      return {}
    },
    newPlanCurrently() {
      return this.currentPlanUpgrade === this.planNo && [PLAN_NO.OPTIMIZE, PLAN_NO.BUILD].includes(this.planNo) && !this.isDevelopmentStore
    },
    planInDevelopment() {
      return this.planNo === PLAN_NO.DEVELOPMENT_STORE;
    },
    planInTrial() {
      return this.planNo === PLAN_NO.TRIAL;
    },
    planInTrial2022() {
      return this.planNo === PLAN_NO.TRIAL2022;
    },
    planInStarter() {
      return this.planNo === PLAN_NO.STARTER;
    },
    planInEnterprise() {
      return this.planNo === PLAN_NO.ENTERPRISE;
    },
    planInBuild() {
      return this.planNo === PLAN_NO.BUILD;
    },
    planInAdvancedOptimize() {
      return [PLAN_NO.ADVANCED, PLAN_NO.OPTIMIZE].includes(this.plan)
    },
    planInBuildProfessional() {
      return [PLAN_NO.PROFESSIONAL, PLAN_NO.BUILD].includes(this.plan)
    },
    advancedProfessional() {
      return [PLAN_NO.ADVANCED, PLAN_NO.PROFESSIONAL].includes(this.plan) && !this.isDevelopmentStore
    },
    optimizeEnterprise() {
      return [PLAN_NO.OPTIMIZE, PLAN_NO.ENTERPRISE].includes(this.plan)
    },
    txtPlanMode() {
      return this.yearlyMode ? this.$t('dataNewPlan.planMode.yearly') : this.$t('dataNewPlan.planMode.monthly');
    },
    prePlanTextButton() {
      if (this.isDevelopmentStore && !this.planInDevelopment) {
        if (!this.yearlyMode) return this.$t("dataNewPlan.upgradePlan.monthly")
        return this.$t("dataNewPlan.upgradePlan", { "planMode": this.txtPlanMode })
      }
      
      if ([PLAN_NO.OPTIMIZE, PLAN_NO.ENTERPRISE].includes(this.currentPlanUpgrade)) {
        this.chargePlanActionAttribute = PLAN_ACTION.DOWNGRADE
        if (this.yearlyMode) {
          return this.$t("downgrade.prePlan", { "planMode": this.txtPlanMode })
        }
        return this.$t("downgrade.prePlan.monthly")
      }

      if (!this.yearlyMode) return this.$t("dataNewPlan.upgradePlan.monthly")
      this.chargePlanActionAttribute = PLAN_ACTION.UPGRADE
      return this.$t("dataNewPlan.upgradePlan", { "planMode": this.txtPlanMode })
    },
    currentPlanTextButton() {
      if (this.planInDevelopment) {
        return this.$t("dataNewPlan.currentPlan")
      }

      if (this.isProfessionalPlan) {
        this.chargePlanActionAttribute = PLAN_ACTION.CURRENT
        return this.$t("dataNewPlan.currentPlan")
      }

      if (this.trialExpire) {
        return this.$t("pricing.expiredPlan")
      }

      if (this.planInStarter) {
        this.chargePlanActionAttribute = PLAN_ACTION.CURRENT
        return this.$t("dataNewPlan.currentPlan")
      }

      if (this.yearlyMode && !this.isFreeTrial && !this.isFreeTrial2022) {
        if (!this.maybeChargeYearly) {
          this.chargePlanActionAttribute = PLAN_ACTION.SWITCH_YEARLY
          return this.$t("yearly.switchPlan")
        }
      }

      if (!this.yearlyMode && !this.isFreeTrial && !this.isFreeTrial2022) {
        if (!this.maybeChargeMonthly) {
          this.chargePlanActionAttribute = PLAN_ACTION.SWITCH_MONTHLY
          return this.$t("monthly.switchMonthlyPlan")
        }
      }

      this.chargePlanActionAttribute = PLAN_ACTION.CURRENT
      return this.$t("dataNewPlan.currentPlan")
    },
    priceOfAnnually() {
      return this.checkNumberFormat(this.pricingByPlan(this.planNo, PLAN_TYPE.ANNUALLY)?.price);
    },
    priceOfMonthly() {
      if (this.planInStarter) return PLAN_PRICING_DEFAULT['starter']
      if (this.planInDevelopment) return PLAN_PRICING_DEFAULT['development']

      if (this.yearlyMode && !this.isProfessionalPlan) {
        return this.checkNumberFormat(Math.round(this.priceOfAnnually / 12 * 100) / 100);
      }

      return this.checkNumberFormat(this.pricingByPlan(this.planNo, PLAN_TYPE.MONTHLY)?.price);
    },
    basePriceOfMonthly() {
      if (this.planInStarter) return
      return this.checkNumberFormat(this.pricingByPlan(this.planNo, PLAN_TYPE.MONTHLY)?.basePrice)
    },
    isShowPrioritySupport() {
      if (this.isAdvancePlan) return true;
      return this.yearlyMode && !(this.planInTrial || this.planInTrial2022 || this.planInStarter || this.isProfessionalPlan || this.planInBuild);
    },
  },
});
